import React, { useState, useEffect } from 'react';
import { Music, Heart, Send, Play, Pause, Check, Moon, Sun } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LandingPage = () => {
  const whatsappNumber = '212708922931';
  const whatsappMessage = encodeURIComponent('Bonjour! Je suis intéressé(e) par la création d\'une chanson personnalisée avec Oughniyati.');
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
  
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [darkMode, setDarkMode] = useState(true);

  // Load dark mode preference from localStorage or use dark mode as default
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      const isDark = savedMode === 'true';
      setDarkMode(isDark);
      if (isDark) {
        document.documentElement.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
      }
    } else {
      // Default to dark mode
      document.documentElement.classList.add('dark-mode');
      localStorage.setItem('darkMode', 'true');
    }
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    
    if (newMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header-nav">
        <img 
          src={darkMode 
            ? "https://customer-assets.emergentagent.com/job_souvenir-melodique/artifacts/vr1mabmn_oughniyati_darkmode.png"
            : "https://customer-assets.emergentagent.com/job_souvenir-melodique/artifacts/g1ze29h7_Oughniyati.png"
          }
          alt="Oughniyati Logo" 
          className="logo-image"
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button 
            onClick={toggleDarkMode}
            className="dark-mode-toggle"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary header-whatsapp-btn">
            <Send size={18} />
            Discuter sur WhatsApp
          </a>
        </div>
      </header>

      {/* Floating WhatsApp Button for Mobile */}
      <a 
        href={whatsappLink} 
        target="_blank" 
        rel="noopener noreferrer" 
        className="floating-whatsapp-btn"
        aria-label="Contacter sur WhatsApp"
      >
        <Send size={24} />
      </a>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Heart size={14} />
            <span>Créations uniques · Made in Morocco</span>
          </div>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            Offrez une émotion inoubliable avec Oughniyati : Votre chanson unique, créée sur mesure.
          </h1>
          <p className="body-large" style={{ marginBottom: '2rem', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            Pour les mariages, anniversaires et moments précieux. Nous transformons votre histoire en une musique qui vous appartient.
          </p>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ fontSize: '1rem', padding: '1rem 2rem' }}>
            <Send size={20} />
            Créer ma chanson personnalisée
          </a>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '800px' }}>
          <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>Plus qu'une chanson, un souvenir éternel.</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Les cadeaux standards se perdent et s'oublient. Mais une chanson personnalisée mentionnant des détails précis, des blagues privées et vos moments uniques ? 
            Elle reste pour toujours. Malgré l'utilisation de technologies avancées, chaque création porte votre touche personnelle et votre histoire unique.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>Comment ça marche ?</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            <div className="voice-card accent-pink hover-lift" style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <Heart size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>Racontez votre histoire</h3>
              <p className="body-small">
                Partagez les détails sur la personne et l'occasion via WhatsApp. Plus c'est personnel, mieux c'est !
              </p>
            </div>

            <div className="voice-card accent-blue hover-lift" style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <Music size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>Nous composons votre titre</h3>
              <p className="body-small">
                Oughniyati utilise des technologies avancées pour créer un style musical unique, rien que pour vous.
              </p>
            </div>

            <div className="voice-card accent-orange hover-lift" style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(255, 255, 255, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <Check size={28} color="var(--text-primary)" />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>Recevez votre souvenir unique</h3>
              <p className="body-small">
                Nous vous envoyons le fichier audio (MP3) prêt à être écouté. <strong>En extra :</strong> Une magnifique vidéo avec les paroles défilantes pour partager l'émotion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Audio Samples */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>Écoutez des exemples Oughniyati</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            <AudioSampleCard 
              id="mariage"
              title="Joyeux Mariage" 
              subtitle="à ma sœur"
              accent="accent-purple"
              audioUrl="https://customer-assets.emergentagent.com/job_souvenir-melodique/artifacts/l771blxm_Mbrok%203rsek%20ya%20Khti.mp3"
              currentPlayingId={currentPlayingId}
              setCurrentPlayingId={setCurrentPlayingId}
            />
            <AudioSampleCard 
              id="anniversaire"
              title="Joyeux Anniversaire" 
              subtitle="à ma fille"
              accent="accent-green"
              audioUrl="https://customer-assets.emergentagent.com/job_souvenir-melodique/artifacts/6621l78v_Happy%20Birthday_Mouna_Oughniyati.mp3"
              currentPlayingId={currentPlayingId}
              setCurrentPlayingId={setCurrentPlayingId}
            />
            <AudioSampleCard 
              id="baccalaureat"
              title="Félicitations pour ton Baccalauréat" 
              subtitle="à mon frère"
              accent="accent-orange"
              audioUrl="https://customer-assets.emergentagent.com/job_souvenir-melodique/artifacts/no2g9rg4_Mbrok%20Najah%20ya%20khoya.mp3"
              currentPlayingId={currentPlayingId}
              setCurrentPlayingId={setCurrentPlayingId}
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>Ils ont offert de l'émotion</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
            <div className="voice-card hover-lift">
              <p className="body-medium" style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
                "J'ai commandé une chanson pour le mariage de ma sœur. Quand elle l'a entendue, elle a pleuré de joie. Le processus via WhatsApp était tellement simple et rapide !"
              </p>
              <p className="body-small" style={{ fontWeight: '500', color: 'var(--text-primary)' }}>— Amina L., Rabat</p>
            </div>

            <div className="voice-card hover-lift">
              <p className="body-medium" style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
                "Pour l'anniversaire de ma fille, j'ai voulu lui offrir quelque chose d'unique. La chanson créée par Oughniyati a capturé parfaitement notre relation père-fille. Elle l'écoute tous les soirs avant de dormir !"
              </p>
              <p className="body-small" style={{ fontWeight: '500', color: 'var(--text-primary)' }}>— Karim B., Casablanca</p>
            </div>

            <div className="voice-card hover-lift">
              <p className="body-medium" style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
                "J'ai commandé une chanson pour féliciter mon frère qui a obtenu son baccalauréat. C'était la surprise parfaite ! Il était tellement ému qu'il a partagé la chanson avec tous ses amis. Merci Oughniyati !"
              </p>
              <p className="body-small" style={{ fontWeight: '500', color: 'var(--text-primary)' }}>— Salma K., Marrakech</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>Questions fréquentes</h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" style={{ borderBottom: '1px solid var(--border-light)', padding: '1rem 0' }}>
              <AccordionTrigger className="heading-3" style={{ textAlign: 'left' }}>
                Est-ce vraiment unique ?
              </AccordionTrigger>
              <AccordionContent className="body-medium" style={{ color: 'var(--text-secondary)', paddingTop: '0.5rem' }}>
                Absolument ! Chaque chanson est créée spécifiquement pour votre histoire. Nous intégrons vos détails personnels, vos anecdotes et vos émotions. Aucune chanson ne se ressemble.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" style={{ borderBottom: '1px solid var(--border-light)', padding: '1rem 0' }}>
              <AccordionTrigger className="heading-3" style={{ textAlign: 'left' }}>
                Comment payer ?
              </AccordionTrigger>
              <AccordionContent className="body-medium" style={{ color: 'var(--text-secondary)', paddingTop: '0.5rem' }}>
                Le paiement est simple et sécurisé. Après notre discussion sur WhatsApp, vous pouvez payer par virement bancaire, WafaCash ou Cash Plus. Aucune carte bancaire nécessaire sur le site.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" style={{ borderBottom: '1px solid var(--border-light)', padding: '1rem 0' }}>
              <AccordionTrigger className="heading-3" style={{ textAlign: 'left' }}>
                Combien de temps ça prend ?
              </AccordionTrigger>
              <AccordionContent className="body-medium" style={{ color: 'var(--text-secondary)', paddingTop: '0.5rem' }}>
                Habituellement, votre chanson personnalisée est prête en 24 à 48 heures après la confirmation de tous les détails. Pour les commandes urgentes, contactez-nous sur WhatsApp pour discuter des options.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section" style={{ background: 'var(--gradient-hero-warm)', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: '700px' }}>
          <h2 className="heading-hero" style={{ marginBottom: '1.5rem' }}>Prêt à créer un moment magique avec Oughniyati ?</h2>
          <p className="body-large" style={{ marginBottom: '2rem' }}>
            Transformez vos émotions en musique. Commencez votre projet personnalisé dès maintenant.
          </p>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ fontSize: '1rem', padding: '1rem 2rem' }}>
            <Send size={20} />
            Créer ma chanson personnalisée
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: 'var(--text-primary)', color: 'white', padding: '2rem 0', textAlign: 'center' }}>
        <div className="container">
          <p className="body-small footer-text" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>© 2025 Oughniyati - Des chansons qui racontent votre histoire</p>
          <p className="caption footer-caption" style={{ color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.5rem' }}>Made with ❤️ in Morocco</p>
        </div>
      </footer>
    </div>
  );
};

// Audio Sample Card Component
const AudioSampleCard = ({ id, title, subtitle, accent, audioUrl, currentPlayingId, setCurrentPlayingId }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef(null);

  // Stop playing when another audio starts
  React.useEffect(() => {
    if (currentPlayingId !== id && isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [currentPlayingId, id, isPlaying]);

  const handlePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        setCurrentPlayingId(null);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
        setCurrentPlayingId(id);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentPlayingId(null);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`voice-card ${accent} hover-lift`}>
      <div style={{ marginBottom: '1rem' }}>
        <h3 className="heading-3" style={{ marginBottom: '0.25rem' }}>{title}</h3>
        <p className="body-small">{subtitle}</p>
      </div>
      {audioUrl ? (
        <>
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
          />
          <div className="audio-player">
            <button className="play-button" onClick={handlePlay}>
              {isPlaying ? (
                <Pause size={16} fill="var(--text-primary)" />
              ) : (
                <Play size={16} fill="none" />
              )}
            </button>
            <div className="audio-progress">
              <div style={{ 
                width: `${progress}%`, 
                height: '100%', 
                background: 'var(--text-primary)', 
                borderRadius: '0.125rem',
                transition: 'width 0.1s linear'
              }}></div>
            </div>
            <span className="audio-time">{formatTime(duration)}</span>
          </div>
        </>
      ) : (
        <div className="audio-player">
          <button className="play-button">
            <Play size={16} />
          </button>
          <div className="audio-progress">
            <div style={{ 
              width: '0%', 
              height: '100%', 
              background: 'var(--text-primary)', 
              borderRadius: '0.125rem'
            }}></div>
          </div>
          <span className="audio-time">--:--</span>
        </div>
      )}
    </div>
  );
};

export default LandingPage;